/**
 * **A client's month**, drawn — weeks as rows with their Monday, weekday heads, each day trained,
 * missed, booked, owed-by-the-plan or free, today outlined and — on a lapse — the last training
 * ringed, and each row's tally at its end. `model/monthCalendar` decides every square.
 *
 * It is a `table`: the weekday heads are column headers and each row's Monday is its row header,
 * so a screen reader reads a square as "czwartek, 17 wrz: trening, Góra A" rather than as a run of
 * numbers. The square's state is in words for it too, never in the fill alone.
 */

import { useTranslation } from 'react-i18next';

import { addDays, parseIsoDate } from '~/shared/lib/calendarDate';
import { dateTimeFormat } from '~/shared/lib/intlCache';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useHomeFormats } from '../lib/useHomeFormats';
import type { MonthCalendar as MonthView, MonthCell } from '../model/monthCalendar';
import styles from './taskBlocks.module.css';

const KIND: Readonly<Record<MonthCell['kind'], string | undefined>> = {
  done: styles.cellDone,
  missed: styles.cellMissed,
  planned: undefined,
  ghost: styles.cellGhost,
  rest: undefined,
  ahead: styles.cellAhead,
};

interface MonthCalendarProps {
  readonly month: Extract<MonthView, { kind: 'month' }>;
  /** Ring the last day the client trained — a lapse's fact. */
  readonly ringLastTrained: boolean;
  /** Draw the squares with their session names — the desk; the quick card draws the day alone. */
  readonly named: boolean;
  /** The sentence at the legend's end, where the card has one. */
  readonly note?: string;
}

export function MonthCalendar({ month, ringLastTrained, named, note }: MonthCalendarProps) {
  const { t, i18n } = useTranslation();
  const { shortDate } = useHomeFormats();
  const day = (iso: string, options: Intl.DateTimeFormatOptions) =>
    dateTimeFormat(i18n.language, options).format(parseIsoDate(iso) ?? new Date(0));
  const firstMonday = parseIsoDate(month.rows[0]?.startDay ?? '') ?? new Date(0);
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    dateTimeFormat(i18n.language, { weekday: 'short' }).format(addDays(firstMonday, index)),
  );

  return (
    <div className={styles.month}>
      <table className={hidden.srOnly}>
        <caption>{t('web:tasks.blocks.month.caption')}</caption>
        <tbody>
          {month.rows.map((row) => (
            <tr key={row.startDay}>
              <th scope="row">{shortDate(row.startDay)}</th>
              {row.cells.map((cell) => (
                <td key={cell.day}>
                  {day(cell.day, { weekday: 'long', day: 'numeric', month: 'short' })}:{' '}
                  {t(`web:tasks.blocks.month.kind.${cell.kind}`)}
                  {cell.title === '' ? '' : `, ${cell.title}`}
                  {cell.isToday ? `, ${t('web:tasks.week.today')}` : ''}
                </td>
              ))}
              <td>{row.isAhead ? '' : t('web:tasks.week.ratio', { done: row.done, total: row.owed })}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.monthRow} aria-hidden="true">
        <span />
        {weekdays.map((name) => (
          <span className={styles.monthHead} key={name}>
            {name}
          </span>
        ))}
        <span className={styles.monthHead}>{t('web:tasks.blocks.month.sessions')}</span>
      </div>
      {month.rows.map((row) => (
        <div className={styles.monthRow} key={row.startDay} aria-hidden="true">
          <span className={styles.monthLabel}>{shortDate(row.startDay)}</span>
          {row.cells.map((cell) => (
            <span
              key={cell.day}
              className={`${String(styles.cell)} ${KIND[cell.kind] ?? ''} ${
                cell.isToday ? String(styles.cellToday) : ''
              } ${ringLastTrained && cell.isLastTrained ? String(styles.cellLast) : ''}`}
            >
              <span className={styles.cellDay}>{day(cell.day, { day: 'numeric' })}</span>
              {named ? <span className={styles.cellTitle}>{cellWord(cell)}</span> : null}
              {named && cell.kind === 'ghost' ? (
                <span className={styles.cellHint}>{t('web:tasks.blocks.month.byPlan')}</span>
              ) : null}
            </span>
          ))}
          <b
            className={`${String(styles.monthTally)} ${
              row.isAhead
                ? String(styles.monthTallyAhead)
                : row.owed > 0 && row.done < row.owed && !row.isCurrent
                  ? String(styles.monthTallyShort)
                  : ''
            }`}
          >
            {row.owed === 0 && !row.isAhead ? '0' : t('web:tasks.week.ratio', { done: row.done, total: row.owed })}
          </b>
        </div>
      ))}
      <div className={styles.legend} aria-hidden="true">
        <span className={styles.legendItem}>
          <i className={`${String(styles.legendSwatch)} ${String(styles.legendDone)}`} />
          {t('web:tasks.blocks.month.kind.done')}
        </span>
        <span className={styles.legendItem}>
          <i className={`${String(styles.legendSwatch)} ${String(styles.legendMissed)}`} />
          {t('web:tasks.blocks.month.kind.missed')}
        </span>
        {month.hasGhosts ? (
          <span className={styles.legendItem}>
            <i className={`${String(styles.legendSwatch)} ${String(styles.legendGhost)}`} />
            {t('web:tasks.blocks.month.kind.ghost')}
          </span>
        ) : null}
        {note === undefined ? null : <span className={styles.legendNote}>{note}</span>}
      </div>
    </div>
  );

  function cellWord(cell: MonthCell): string {
    if (cell.kind === 'missed') return t('web:tasks.blocks.month.kind.missed');
    if (cell.title !== '') return cell.title;
    if (cell.isToday) return t('web:tasks.week.today');
    return '';
  }
}
